import { useNavigate } from "react-router-dom";
import { FormEvent, KeyboardEvent, useState } from "react";
import APIService from "../../services/Api";
import { ImSpinner9 } from "react-icons/im";
import { MdError } from "react-icons/md";
import { FaUserPlus, FaUserTie } from "react-icons/fa";
import { BsCashStack } from "react-icons/bs";
import { useAppStateContext } from "src/auth/AppStateContext";
import { HiArrowRight, HiCheckCircle } from "react-icons/hi2";
import isfLogo from "../../assets/images/logo/isflogo-transparent.png";

type RegisterFormParams = {
  disableImage?: boolean;
  disabledBackground?: boolean;
};

const RegisterPreForm = ({
  disableImage = false,
  disabledBackground = true,
}: RegisterFormParams) => {
  const [response, setResponse] = useState<string>("");
  const [cashierApodo, setCashierApodo] = useState<string>("");
  const { setShowRegisterPopup } = useAppStateContext();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const onChangeCode = (e: FormEvent<HTMLInputElement>) => {
    setResponse("");
    setCashierApodo(e.currentTarget.value.trim());
  };

  const verifyCode = async () => {
    if (!cashierApodo.trim() || loading) return;

    setLoading(true);
    setResponse("");

    let cc = cashierApodo.trim();
    if (!cc.includes("-") && cc.length === 6) {
      cc = `${cc.substring(0, 3)}-${cc.substring(3)}`;
    }

    const res = await APIService.systemVerifyCashierApodo({ apodo: cc });
    const registerLink = res?.data?.data?.registerlink;
    setResponse(registerLink);

    if (registerLink !== "notfound") {
      setShowRegisterPopup(false);
      navigate(`/${cc}`);
    }

    setLoading(false);
  };

  const handleGoRegister = (url: string) => {
    setShowRegisterPopup(false);
    navigate(url);
  };

  const handleApodoKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      verifyCode();
    }
  };

  const cashierBenefits = [
    "Registra tus porcentajes de ganancias.",
    "Elige bancos y wallets con los que trabajar.",
    "Obtén tu propio dashboard de operaciones.",
  ];

  const rootClassName = disabledBackground
    ? "w-full"
    : "min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4";

  const cardClassName =
    "rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col h-full";

  const primaryBtnClassName =
    "w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-green-admin to-green-admin-dark px-5 py-3 text-sm sm:text-base font-semibold text-white shadow-md transition-all duration-200 hover:from-green-admin-dark hover:to-green-admin hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-green-admin/40 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100";

  return (
    <section className={rootClassName}>
      <div className="w-full max-w-5xl mx-auto px-3 sm:px-5 py-4 sm:py-6">
        {!disableImage && (
          <div className="flex flex-col items-center text-center mb-5 sm:mb-6">
            <img src={isfLogo} alt="ISF" className="w-10 sm:w-12 h-auto mb-3" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Únete a ISF</h1>
            <p className="text-sm sm:text-base text-gray-500 mt-1 max-w-md">
              Elige cómo deseas registrarte y comienza en pocos pasos.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          {/* Cliente */}
          <div className={cardClassName}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-green-admin to-green-admin-dark flex items-center justify-center shadow-md flex-shrink-0">
                <FaUserPlus className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Unirse como Cliente</h2>
                <p className="text-xs sm:text-sm text-gray-500">Para enviar y recibir saldos</p>
              </div>
            </div>

            {!loading ? (
              <div className="flex flex-col gap-4 flex-1">
                <button
                  type="button"
                  onClick={() => handleGoRegister("/registro-usuario")}
                  className={`${primaryBtnClassName} mt-auto`}
                >
                  Registrarme sin apodo
                  <HiArrowRight size={18} />
                </button>

                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-800 mb-1">
                    ¿Tienes el apodo de un cajero?
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 mb-3">
                    Escríbelo aquí para vincular tu registro directamente.
                  </p>

                  <label htmlFor="cashier-apodo" className="sr-only">
                    Apodo del cajero
                  </label>
                  <input
                    id="cashier-apodo"
                    type="text"
                    value={cashierApodo}
                    placeholder="Ej: abc-123"
                    onChange={onChangeCode}
                    onKeyDown={handleApodoKeyDown}
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-green-admin focus:outline-none focus:ring-2 focus:ring-green-admin/25"
                  />

                  <button
                    type="button"
                    onClick={verifyCode}
                    disabled={!cashierApodo.trim()}
                    className={`${primaryBtnClassName} mt-3`}
                  >
                    Verificar y continuar
                  </button>

                  {response === "notfound" && (
                    <div className="mt-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
                      <MdError className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm font-medium text-red-700">
                        El apodo ingresado no fue encontrado.
                      </span>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-400 text-center">
                  Si no conoces un cajero, usa la opción de arriba y elige uno después.
                </p>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center py-10">
                <div className="text-center">
                  <ImSpinner9 className="animate-spin text-green-admin mx-auto mb-3" size={36} />
                  <p className="text-sm font-medium text-gray-600">Verificando apodo...</p>
                </div>
              </div>
            )}
          </div>

          {/* Cajero */}
          <div className={cardClassName}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-green-admin to-green-admin-dark flex items-center justify-center shadow-md flex-shrink-0">
                <FaUserTie className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Unirse como Cajero</h2>
                <p className="text-xs sm:text-sm text-gray-500">Gana dinero con comisiones por cada operación</p>
              </div>
            </div>

            <ul className="space-y-3 mb-5 flex-1">
              {cashierBenefits.map((benefit) => (
                <li key={benefit} className="flex items-start gap-2.5 text-sm text-gray-600">
                  <HiCheckCircle className="text-green-admin flex-shrink-0 mt-0.5" size={18} />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => handleGoRegister("/registro-cajero")}
              className={primaryBtnClassName}
            >
              <BsCashStack size={18} />
              Registrarme como Cajero
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegisterPreForm;
